import { useMemo } from 'react';
import {
  percentFormatter,
  PercentFormatterOptions,
} from 'react-formatted-input-hook';

import { useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';

type Params = {
  options?: Omit<PercentFormatterOptions, 'liveUpdate' | 'onChange' | 'value'>;
};

/**
 * Returns a memoized percent formatter based on the provided options.
 *
 * @param options - An optional object containing options to customize the formatting.
 * @returns A memoized percent formatter.
 */
export const usePercentFormatter = (params?: Params) => {
  const options = params?.options ?? {};

  const { i18n } = useTranslation('@myn/mui');

  const { getProfileField } = useProfile();

  const locale =
    getProfileField('formattingConfig')?.numbersFormat || i18n.language;

  return useMemo(() => {
    const fmt = percentFormatter({
      locales: locale,
      max: Infinity,
      min: -Infinity,
      ...options,
    });
    return {
      format: (value: string | number) =>
        fmt.format?.(fmt.onBlur?.(`${value}`) ?? ''),
      value: fmt.value,
    };
  }, [locale, ...Object.values(options)]);
};

/**
 * A component that displays a number as a percentage with static formatting.
 *
 * @param number - The number to be formatted as a percentage.
 * @param options - An optional object containing options to customize the formatting.
 * @param options.decimal - The number of decimal places to display. Defaults to 2.
 * @param options.groupSeparator - The character to use as the group separator. Defaults to ','.
 * @param options.decimalSeparator - The character to use as the decimal separator. Defaults to '.'.
 * @param options.prefix - The prefix to display before the formatted number.
 * @param options.suffix - The suffix to display after the formatted number.
 * @param options.formatNegative - The format to use for negative numbers. Can be 'minus' or 'parentheses'. Defaults to 'minus'.
 * @param options.formatZero - The format to use for zero values. Can be 'keep' or 'hide'. Defaults to 'keep'.
 * @returns A React component that displays the formatted percentage.
 *
 * @example
 * ```tsx
 * <StaticPercentSpan number={0.5} />
 * ```
 */
const StaticPercentSpan = ({
  number,
  options = {},
}: {
  number: number | string;
  options?: Omit<PercentFormatterOptions, 'liveUpdate' | 'onChange' | 'value'>;
}) => {
  const formatter = usePercentFormatter({
    options,
  });

  const res = formatter.format?.(`${number}`);

  return <span>{res?.formatted ?? ''}</span>;
};

export default StaticPercentSpan;
